/**
 * One place that knows how an identity (face) photo is put on the wire.
 *
 * Identity photos are cropped in the browser before upload, so the photo the operator actually
 * picked only exists client-side — once the request leaves, it is gone for good. Roughly twenty
 * call sites used to hand-roll `new FormData(); fd.append('photo', blob, name)`, which is why
 * sending the original alongside it had twenty places to be forgotten. They all go through here
 * instead.
 *
 * Only identity photos carry an original. Document, vehicle, pet and announcement photos keep
 * using their own plain FormData — their framing is not worth preserving, and adding a second
 * file to those requests would double the upload for nothing.
 */

import type { PhotoResult } from '../types/photo';

/** Must match IdentityPhotoOriginalFormField in the backend's identity_photo_original.go. */
export const ORIGINAL_PHOTO_FIELD = 'original_photo';

/** Must match the field every identity-photo handler reads via FormFile("photo"). */
export const PHOTO_FIELD = 'photo';

/**
 * Appends a captured photo to `formData`: the crop under `photo`, and — only when the modal
 * captured one — the untouched source under `original_photo`.
 *
 * When there is no original, the result is byte-for-byte the request shape that shipped before
 * originals existed. That is deliberate: an absent original must never change the request, so
 * older backends and clients stay compatible in both directions.
 *
 * @param baseName filename stem for both parts; the original gets a `-original` suffix so the
 *                 two are distinguishable in logs and server-side temp files.
 */
export function appendIdentityPhoto(
  formData: FormData,
  photo: Pick<PhotoResult, 'blob' | 'original'>,
  baseName = 'photo'
): void {
  formData.append(PHOTO_FIELD, photo.blob, `${baseName}.jpg`);

  if (photo.original?.blob) {
    formData.append(ORIGINAL_PHOTO_FIELD, photo.original.blob, `${baseName}-original.jpg`);
  }
}

export interface IdentityPhotoFormOptions {
  /** Required by most identity-photo endpoints, which read it from the form or the query. */
  customerId?: string;
  /** Defaults to 'facial_recognition' server-side when omitted. */
  photoType?: string;
}

/**
 * Builds a complete identity-photo FormData, including the fields the handlers expect.
 *
 * Optional fields are omitted rather than sent empty: the handlers treat an empty customer_id
 * as a validation error, not as "absent".
 */
export function buildIdentityPhotoFormData(
  photo: Pick<PhotoResult, 'blob' | 'original'>,
  options: IdentityPhotoFormOptions = {},
  baseName = 'photo'
): FormData {
  const formData = new FormData();
  appendIdentityPhoto(formData, photo, baseName);

  if (options.customerId) {
    formData.append('customer_id', options.customerId);
  }
  if (options.photoType) {
    formData.append('photo_type', options.photoType);
  }

  return formData;
}
